import { BehaviorSubject } from 'rxjs';
import { debounce, type DebouncedFunc } from 'lodash';

import { deferred } from '@flintmint/core-shared/utils';
import { getAuthUser } from '@flintmint/web-shared/hooks/use-auth-user';

import { createWorker } from './worker-factory';
import { activeIndexKeyFor, archivedIndexKeyFor, allIndexKeysFor } from './indexeddb-utils';
import type { SearchableDocument } from './orama-schema';
import {
  PERSIST_FAILED_ACTION,
  PERSISTED_INDEX_ACTION,
  RESTORE_FAILED_ACTION,
  type OramaSearchParams,
  type RawSearchDoc,
  type SearchOptions,
  type SearchResponse,
  type WorkerRequest,
  type WorkerResponse,
} from './types';

export type IndexStatus = 'initializing' | 'ready' | 'rebuilding' | 'restore-failed';

export type SearchServiceConfig = {
  // Which of the user's two slots this instance owns. The key itself is only known once the
  // user is: the active instance spawns its worker at module eval, before login resolves.
  buildIndexKey: (userId: string) => string;
};

export class SearchService {
  private worker: Worker | null = null;
  // The key the running worker was initialised with; null while no user was known.
  private indexKey: string | null = null;
  // The running worker's one-time answer to whether it found a persisted index (its
  // 'persistedIndex' announce). Settled false when the worker goes away before answering, so a
  // caller waiting on it is never stranded.
  private persistedIndexAnswer: ReturnType<typeof deferred<boolean>> | null = null;
  // Whether the startup index decision (restore, rebuild, or let sync build it) was already
  // made for the running worker; see claimStartupIndexDecision.
  private startupIndexDecisionClaimed = false;
  private _indexUpdateNotifier = new BehaviorSubject<string | null>(null);
  private _indexStatus = new BehaviorSubject<IndexStatus>('initializing');
  private pendingPromises: Map<
    string,
    {
      resolve: (value: any) => void;
      reject: (reason: Error) => void;
      timeout: NodeJS.Timeout;
    }
  > = new Map();
  private MESSAGE_TIMEOUT_MS = 30000; // 30 sec timeout for worker messages

  private restartCount = 0;
  private readonly MAX_RESTARTS = 3;

  private debouncedNotify: DebouncedFunc<() => void>;
  private readonly DEBOUNCE_TIME_MS = 500;
  private lastNotificationTime = 0;

  constructor(private config: SearchServiceConfig) {
    this.debouncedNotify = debounce(
      () => {
        const timestamp = Date.now();
        const notificationId = `indexUpdated-${timestamp}`;

        this._indexUpdateNotifier.next(notificationId);
        this.lastNotificationTime = timestamp;
      },
      this.DEBOUNCE_TIME_MS,
      { leading: false, trailing: true }
    );
  }

  // Idempotent worker spawn for the CURRENT user. The main instance calls this at module
  // scope (preserving the previous eager-constructor spawn) — before login resolves on a
  // fresh sign-in, so it spawns with no key — and LocalDataProvider calls it again on mount:
  // a worker running on another key (or none) is replaced rather than reused, so it can
  // never read or write another account's index. The archived instance stays dormant until
  // the first archive open (localDataService.ensureArchivedSearchIndex).
  ensureStarted(): void {
    const { indexKey } = this.indexKeysForCurrentUser();
    if (this.worker && this.indexKey === indexKey) {
      return;
    }

    if (this.worker) {
      this.terminate();
    }
    this.ensureWorkerInitialized();
  }

  // Resolved fresh on every spawn rather than cached, so a worker can never come back up on
  // a key the signed-in user no longer owns.
  private indexKeysForCurrentUser(): { indexKey: string | null; validIndexKeys: string[] } {
    const { id: userId } = getAuthUser();
    if (!userId) {
      return { indexKey: null, validIndexKeys: [] };
    }

    return { indexKey: this.config.buildIndexKey(userId), validIndexKeys: allIndexKeysFor(userId) };
  }

  private initWorker(): void {
    try {
      const { indexKey, validIndexKeys } = this.indexKeysForCurrentUser();
      this.worker = createWorker();
      // Only once a worker exists: the key must never outlive one, or a dead service would
      // still answer hasPersistedIndex() about a slot nothing is serving.
      this.indexKey = indexKey;
      this.persistedIndexAnswer = deferred<boolean>();
      // A respawn after a crash (restartCount > 0) keeps the decision taken: that worker starts
      // empty and its restore-failed heal rebuilds the index, so deciding again would rebuild twice.
      if (this.restartCount === 0) {
        this.startupIndexDecisionClaimed = false;
      }
      this.worker.onmessage = this.handleWorkerMessage.bind(this);
      this.worker.onerror = this.handleWorkerError.bind(this);
      // First message after every (re)spawn — including the handleWorkerError restart and
      // the post-terminate respawn via ensureWorkerInitialized: delivers the persistence
      // slot; the worker defers its restore until this arrives. A non-zero restart count
      // means this spawn replaces a worker that died, so the fresh one starts empty and
      // announces restore-failed, which routes it into the provider's heal rebuild.
      this.sendWorkerMessage('init', {
        payload: { indexKey, validIndexKeys, skipRestore: this.restartCount > 0 },
      }).catch((error) => {
        // Benign: terminate() landing before the init ack rejects every pending promise,
        // including this one, with this exact reason — a normal logout/restart shutdown,
        // not a real failure. Logging it would spam Sentry via captureConsoleIntegration.
        if (error.message === 'Search service terminated') {
          return;
        }
        console.error('Failed to deliver search worker init:', error);
      });
    } catch (error) {
      console.error('Failed to initialize search worker:', error);
      this.worker = null;
      // A synchronous construction failure (e.g. blocked by browser policy) consumes
      // the same restart budget as an async load failure, so the breaker still trips.
      this.restartCount++;
      if (this.restartCount >= this.MAX_RESTARTS) {
        console.error(`Search worker failed to load after ${this.MAX_RESTARTS} attempts. Giving up.`);
      }
      this.rejectAllPendingPromises('Search worker initialization failed');
    }
  }

  search(searchParams: OramaSearchParams, options: SearchOptions = {}): Promise<SearchResponse> {
    return this.sendWorkerMessage('search', {
      payload: { ...searchParams, ...options },
    });
  }

  // Batch methods: one worker message per batch; the worker builds the
  // SearchableDocuments, keeping the HTML strip / shaping off the main thread.
  addDocs(docs: RawSearchDoc[]): Promise<void> {
    return this.sendWorkerMessage('addDocs', { payload: docs });
  }

  updateDocs(docs: RawSearchDoc[]): Promise<void> {
    return this.sendWorkerMessage('updateDocs', { payload: docs });
  }

  removeDocs(docIds: string[]): Promise<void> {
    return this.sendWorkerMessage('removeDocs', { payload: docIds });
  }

  // Single-doc update taking a pre-built SearchableDocument. Kept for the
  // tag-rename fix-up path in tagNameCache (search + updateDoc round-trip).
  updateDoc(id: string, doc: SearchableDocument): Promise<void> {
    return this.sendWorkerMessage('updateDoc', { id, payload: doc });
  }

  private sendWorkerMessage(action: string, data: any = {}): Promise<any> {
    const messageId = crypto.randomUUID();

    return new Promise((resolve, reject) => {
      if (!this.ensureWorkerInitialized()) {
        reject(new Error('Search worker not available'));
        return;
      }

      const timeout = setTimeout(() => {
        if (this.pendingPromises.has(messageId)) {
          this.pendingPromises.delete(messageId);
          reject(new Error(`Search worker message timeout for action ${action}`));
        }
      }, this.MESSAGE_TIMEOUT_MS);

      this.pendingPromises.set(messageId, { resolve, reject, timeout });

      const request: WorkerRequest = {
        action,
        messageId,
        ...data,
      };
      this.worker!.postMessage(request);
    });
  }

  private ensureWorkerInitialized(): boolean {
    if (this.worker) {
      return true;
    }

    // Circuit breaker: once the restart budget is exhausted, stop re-creating
    // doomed workers on every request (each failed load logs a Sentry error).
    if (this.restartCount >= this.MAX_RESTARTS) {
      return false;
    }

    try {
      this.initWorker();
      return !!this.worker;
    } catch (error) {
      console.error('Failed to reinitialize search worker:', error);
      return false;
    }
  }

  private handleWorkerMessage(response: MessageEvent<WorkerResponse>) {
    // Any message (including the status-only startup announce, which has no
    // messageId) proves the worker is alive: reset the restart budget.
    this.restartCount = 0;

    const { action, messageId, success, result, error, indexUpdated, indexStatus, persistedIndex } = response.data;

    if (action === PERSISTED_INDEX_ACTION) {
      this.persistedIndexAnswer?.resolve(persistedIndex ?? false);
      return;
    }

    // A background persist failed in the worker with nothing awaiting it. Logging it here
    // is what gets it to Sentry — the worker's own console is outside the console capture.
    if (action === PERSIST_FAILED_ACTION) {
      console.error('Failed to persist search index:', error);
      return;
    }

    if (action === RESTORE_FAILED_ACTION) {
      console.error('Failed to restore saved search index:', error);
      return;
    }

    // The worker's one-time announce arrives async; if a rebuild already started on the
    // main thread it must not clobber 'rebuilding' and open the search gate mid-rebuild —
    // the rebuild's own completion sets 'ready' via setIndexStatus.
    if (indexStatus && this._indexStatus.value !== 'rebuilding') {
      this._indexStatus.next(indexStatus);
    }

    if (indexUpdated) {
      this.notifyRenderers();
    }

    if (!messageId) {
      return;
    }

    const pendingPromise = this.pendingPromises.get(messageId);
    if (!pendingPromise) {
      console.warn(`🔧 No pending promise found for ${messageId}`);
      return;
    }

    clearTimeout(pendingPromise.timeout);
    if (success) {
      pendingPromise.resolve(result);
    } else {
      pendingPromise.reject(error ?? new Error('Unknown error'));
    }
    this.pendingPromises.delete(messageId);
  }

  private notifyRenderers() {
    const now = Date.now();

    // Always allow the first notification or notifications after the debounce period
    if (now - this.lastNotificationTime >= this.DEBOUNCE_TIME_MS || this.lastNotificationTime === 0) {
      // Immediate notification
      const notificationId = `indexUpdated-${now}`;
      this._indexUpdateNotifier.next(notificationId);
      this.lastNotificationTime = now;
    } else {
      // Schedule a debounced notification
      this.debouncedNotify();
    }
  }

  private handleWorkerError(error: Event | ErrorEvent) {
    const message = error instanceof ErrorEvent ? error.message : 'Unknown worker load error';
    console.error('Search worker error:', message);
    this.rejectAllPendingPromises(`Worker error: ${message}`);

    // Attempt to recover by restarting the worker, up to MAX_RESTARTS times
    this.terminate();
    if (this.restartCount < this.MAX_RESTARTS) {
      this.restartCount++;
      this.initWorker();
    } else {
      console.error(`Search worker failed to load after ${this.MAX_RESTARTS} attempts. Giving up.`);
    }
  }

  private rejectAllPendingPromises(reason: string) {
    this.pendingPromises.forEach(({ reject, timeout }) => {
      clearTimeout(timeout);
      reject(new Error(reason));
    });
    this.pendingPromises.clear();
  }

  get indexUpdateNotifier() {
    return this._indexUpdateNotifier;
  }

  get indexStatus() {
    return this._indexStatus;
  }

  // The data layer marks the index rebuilding while it re-feeds every doc, then ready once
  // the rebuild is persisted (see localDataService.rebuildSearchIndex). The worker owns the
  // 'ready'/'restore-failed' transitions at startup; terminate() resets to 'initializing'.
  setIndexStatus(status: Extract<IndexStatus, 'rebuilding' | 'ready'>): void {
    this._indexStatus.next(status);
  }

  async hasPersistedIndex(): Promise<boolean> {
    // Whatever slot the running worker owns. Callers ask this after LocalDataProvider's
    // mount-time ensureStarted, so it is already the signed-in user's key; with no user
    // there is no slot and therefore nothing persisted.
    if (!this.indexKey || !this.persistedIndexAnswer) {
      return false;
    }

    // Answered by the worker from the same read its restore uses, never a separate lookup
    // here: another tab could delete or write the index between two reads, and a warm start
    // on an index the worker never restored would serve, then persist, an empty one.
    return this.persistedIndexAnswer.promise;
  }

  // True for the first caller per worker. The worker's persisted-index answer describes what it
  // found at its own start, so only that first decision may act on it: a remounted provider
  // reusing the worker would otherwise rebuild an index the worker already restored or built.
  claimStartupIndexDecision(): boolean {
    if (this.startupIndexDecisionClaimed) {
      return false;
    }
    this.startupIndexDecisionClaimed = true;
    return true;
  }

  replicationCompleted(options: { persistIndex: boolean }): Promise<void> {
    return this.sendWorkerMessage('replicationCompleted', { payload: options.persistIndex });
  }

  // Rebuild bracket: rebuildStarted suppresses batch persists in the worker so a
  // partial index never persists during a rebuild (I1); rebuildFinished re-enables
  // them and persists the now-complete index. See localDataService.rebuildSearchIndex.
  rebuildStarted(): Promise<void> {
    return this.sendWorkerMessage('rebuildStarted');
  }

  // A rebuild snapshot chunk. Unlike addDocs, the worker skips ids removed since
  // rebuildStarted: the chunk's snapshot may predate a live removeDocs, and re-adding
  // the doc would resurrect it in the index persisted at rebuildFinished.
  rebuildDocs(docs: RawSearchDoc[]): Promise<void> {
    return this.sendWorkerMessage('rebuildDocs', { payload: docs });
  }

  rebuildFinished(): Promise<void> {
    return this.sendWorkerMessage('rebuildFinished');
  }

  // Failed-rebuild cleanup: the worker stops recording removed ids but keeps persists
  // suppressed, so the partial index still never persists.
  rebuildAborted(): Promise<void> {
    // No worker (e.g. after terminate): never spawn one — a fresh worker has nothing to clear.
    if (!this.worker) {
      return Promise.resolve();
    }
    return this.sendWorkerMessage('rebuildAborted');
  }

  // Reconcile bracket (archived index): reconcileStarted suppresses batch persists and
  // begins tracking seen ids; reconcileDocs streams RxDB truth in chunks (the worker
  // diffs by id+updatedAt and skips prep for unchanged docs); reconcileFinished removes
  // never-seen index entries, opens the persist gate (this worker never receives
  // replicationCompleted), and persists the reconciled index. See
  // localDataService.reconcileArchivedSearchIndex.
  reconcileStarted(): Promise<void> {
    return this.sendWorkerMessage('reconcileStarted');
  }

  reconcileDocs(docs: RawSearchDoc[]): Promise<void> {
    return this.sendWorkerMessage('reconcileDocs', { payload: docs });
  }

  reconcileFinished(): Promise<void> {
    return this.sendWorkerMessage('reconcileFinished');
  }

  terminate() {
    this.debouncedNotify.cancel();
    this.rejectAllPendingPromises('Search service terminated');
    this._indexStatus.next('initializing');

    if (this.worker) {
      // Detach the handler first: a status announce already queued on the main-thread event
      // loop from the old worker would otherwise be delivered AFTER this reset and re-seed the
      // next session's status.
      this.worker.onmessage = null;
      this.worker.terminate();
      this.worker = null;
      this.indexKey = null;
      this.persistedIndexAnswer?.resolve(false);
      this.persistedIndexAnswer = null;
    }
  }
}

export const searchService = new SearchService({ buildIndexKey: activeIndexKeyFor });
searchService.ensureStarted();

// Archived-docs index (DM-1460): worker spawn is deferred until the first archive open.
export const archivedSearchService = new SearchService({ buildIndexKey: archivedIndexKeyFor });
